<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { laudemioTypes } from '../composables/salesPropertyFormOptions';
import SalesPropertyDocumentsSection from './SalesPropertyDocumentsSection.vue';

const props = defineProps({
  canEdit: { type: Boolean, default: false },
  canEditDocuments: { type: Boolean, default: false },
  transaction: { type: Object, required: true },
});

const emit = defineEmits(['edit']);
const { t } = useI18n();
const profile = computed(() => props.transaction.profile || {});
const laudemios = computed(() => profile.value.laudemios || []);
const typeLabels = computed(() =>
  Object.fromEntries(laudemioTypes(t).map(item => [item.value, item.label]))
);
const valueLabels = computed(() => ({
  1: t('SALES_PROPERTY.LAUDEMIO.SUMMARY.RIP'),
  3: t('SALES_PROPERTY.LAUDEMIO.SUMMARY.FAMILY'),
  4: t('SALES_PROPERTY.LAUDEMIO.SUMMARY.CHURCH'),
}));
</script>

<template>
  <LaisCard
    :title="t('SALES.DETAIL.PROPERTY_SECTIONS.LAUDEMIO')"
    prefix-icon="i-lucide-waves"
  >
    <div v-if="laudemios.length" class="grid gap-4">
      <div class="grid gap-1" data-testid="laudemio-summary-quantity">
        <p class="text-xs font-semibold text-lais-text-muted">
          {{ t('SALES_PROPERTY.LAUDEMIO.SUMMARY.QUANTITY') }}
        </p>
        <p class="text-sm font-semibold text-lais-text">
          {{ laudemios.length }}
        </p>
      </div>
      <div
        v-for="(item, index) in laudemios"
        :key="index"
        data-testid="laudemio-summary-item"
        class="grid gap-3 rounded-lais-sm bg-lais-bg-soft p-3 sm:grid-cols-2"
      >
        <div class="grid gap-1">
          <p class="text-xs font-semibold text-lais-text-muted">
            {{ t('SALES_PROPERTY.LAUDEMIO.SUMMARY.TYPE') }}
          </p>
          <p class="text-sm text-lais-text-body">
            {{ typeLabels[item.tipo_laudemio] || item.tipo_laudemio }}
          </p>
        </div>
        <div v-if="String(item.tipo_laudemio) !== '2'" class="grid gap-1">
          <p class="text-xs font-semibold text-lais-text-muted">
            {{ valueLabels[item.tipo_laudemio] }}
          </p>
          <p class="text-sm text-lais-text-body">
            {{ item.valor_laudemio || t('SALES.DETAIL.EMPTY_VALUE') }}
          </p>
        </div>
      </div>
    </div>
    <p v-else class="text-sm text-lais-text-muted">
      {{ t('SALES_PROPERTY.LAUDEMIO.SUMMARY.EMPTY') }}
    </p>
    <div v-if="canEdit" class="mt-4 flex justify-end">
      <LaisPrimaryButton
        data-test="sales-detail-edit-laudemio"
        icon="i-lucide-pencil"
        variant="link"
        size="sm"
        @click="emit('edit', 'laudemio')"
      >
        {{ t('SALES_PROPERTY.ACTIONS.EDIT') }}
      </LaisPrimaryButton>
    </div>
  </LaisCard>

  <LaisCard
    :title="t('SALES.DETAIL.PROPERTY_SECTIONS.CLAUSES')"
    prefix-icon="i-lucide-file-pen-line"
  >
    <div class="grid gap-4 text-sm leading-relaxed text-lais-text-body">
      <div class="grid gap-1" data-testid="property-clause-secondary">
        <p class="text-xs font-semibold text-lais-text-muted">
          {{ t('SALES_PROPERTY.CLAUSES.SECONDARY') }}
        </p>
        <p>{{ profile.clausula_segunda || t('SALES.DETAIL.EMPTY_VALUE') }}</p>
      </div>
      <div class="grid gap-1" data-testid="property-clause-tenth">
        <p class="text-xs font-semibold text-lais-text-muted">
          {{ t('SALES_PROPERTY.CLAUSES.TENTH') }}
        </p>
        <p>{{ profile.clausula_decima || t('SALES.DETAIL.EMPTY_VALUE') }}</p>
      </div>
    </div>
    <div v-if="canEdit" class="mt-4 flex justify-end">
      <LaisPrimaryButton
        data-test="sales-detail-edit-clauses"
        icon="i-lucide-pencil"
        variant="link"
        size="sm"
        @click="emit('edit', 'clauses')"
      >
        {{ t('SALES_PROPERTY.ACTIONS.EDIT') }}
      </LaisPrimaryButton>
    </div>
  </LaisCard>

  <SalesPropertyDocumentsSection
    :transaction-id="transaction.id"
    :property="transaction.property || {}"
    :can-edit="false"
    :details-label="canEditDocuments ? t('SALES_PROPERTY.ACTIONS.EDIT') : ''"
    details-icon="i-lucide-pencil"
    details-test="sales-detail-edit-documents"
    :show-guidance="false"
    summary
    @details="emit('edit', 'documents')"
  />
</template>
